import { useMutation, useQueryClient } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';
import type {
  PutProgramsIdAlignmentForm200,
  PutProgramsIdAlignmentFormMutationRequest,
} from '@eureca/recruiter-api-sdk/types';
import { alignmentFormQueryKey } from './alignment-form-query';

export interface AlignmentViolation {
  code: string;
  path: string;
  name?: string;
}

/**
 * Erro tipado do `PUT`. `code === 'alignment_form_invalid'` traz `violations`
 * (contrato 422 do Core preservado pelo BFF); o container trata
 * `no_visible_page` como mensagem inline (AC-35) e o resto como Sonner (AC-48).
 */
export class AlignmentFormMutationError extends Error {
  readonly name = 'AlignmentFormMutationError';
  readonly code: string | null;
  readonly violations: AlignmentViolation[];
  constructor(
    message: string,
    readonly status: number,
    body: unknown
  ) {
    super(message);
    const b = (body ?? {}) as { code?: unknown; violations?: unknown };
    this.code = typeof b.code === 'string' ? b.code : null;
    this.violations = Array.isArray(b.violations) ? (b.violations as AlignmentViolation[]) : [];
  }

  hasViolation(code: string): boolean {
    return this.violations.some((v) => v.code === code);
  }
}

export function usePutAlignmentForm(programId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: PutProgramsIdAlignmentFormMutationRequest
    ): Promise<PutProgramsIdAlignmentForm200> => {
      const res = await fetchClient<PutProgramsIdAlignmentForm200>({
        method: 'PUT',
        url: `/programs/${programId}/alignment-form`,
        data: input,
      });
      if (res.error) {
        throw new AlignmentFormMutationError(
          `Failed to save alignment form (status ${res.error.status})`,
          res.error.status,
          res.error.error
        );
      }
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(alignmentFormQueryKey(programId), data);
    },
  });
}
